import React, { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import {
  AdminAsyncJobOutDTO,
  AdminJobsFilter,
  JobCategoryEnum,
  JobStatusEnum,
  JobTypeEnum,
  Pageable,
} from '@api/ringil-api.v2.tsx';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgRepeatIcon from '@app/icons/admin/icon/RepeatIcon.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';

const ACTIVE: JobStatusEnum[] = [JobStatusEnum.PENDING, JobStatusEnum.RUNNING];

/** Admin cross-company async jobs monitoring: paged + filtered, mirrors the customer jobs list. */
const JobsMonitoringPage: React.FC = () => {
  const { adminListJobsPaged, cancelAsyncJob } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: AdminAsyncJobOutDTO[]; clear: () => void }>(null);

  // Admin sees all companies, so the company-jobs push channel doesn't apply — plain slow poll.
  useEffect(() => {
    const id = window.setInterval(() => tableRef.current?.reloadData(), 30000);
    return () => window.clearInterval(id);
  }, []);

  const loadDataApi = (filter: AdminJobsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<AdminAsyncJobOutDTO>) => void) => {
    adminListJobsPaged(filter, pageable, data => {
      onSuccess({ data: data.data, total: data.total, page: data.page, size: data.size });
    });
  };

  const typeLabel = (jobType: JobTypeEnum) => tr(`JobList.type.${jobType}`, `${jobType}`);
  const categoryLabel = (category: JobCategoryEnum) => tr(`JobList.category.${category}`, `${category}`);

  const renderJobColumn = (row: AdminAsyncJobOutDTO) => (
    <div className={'flex flex-col gap-1'}>
      <Link to={nav.createNavigationLink(nav.urlFunctions.createJobDetail(row.job.jobId))} className={'text-content-brand'}>
        {typeLabel(row.job.jobType)}
        {row.job.applicationId != null && ` ${row.job.applicationId}`}
      </Link>
      <span className={'text-sm text-content-tertiary'}>{row.job.jobId}</span>
      {row.archived && (
        <COBadge variant={'outlined'} type={'draft'}>
          {tr('AdminJobs.archived', 'Archived')}
        </COBadge>
      )}
    </div>
  );

  const renderCompanyColumn = (row: AdminAsyncJobOutDTO) => (
    <div className={'flex flex-col gap-1'}>
      <span className={'text-content-primary'}>{row.companyName ?? row.companyId}</span>
      <span className={'text-sm text-content-tertiary'}>{row.createdByEmail ?? row.createdBy}</span>
    </div>
  );

  const renderStateColumn = (row: AdminAsyncJobOutDTO) => {
    const job = row.job;
    const badge = () => {
      switch (job.jobStatus) {
        case JobStatusEnum.DONE:
          return job.failedCount > 0 ? (
            <COBadge variant={'filled'} type={'warning'}>{`DONE (${job.failedCount} failed)`}</COBadge>
          ) : (
            <COBadge variant={'filled'} type={'positive'}>DONE</COBadge>
          );
        case JobStatusEnum.ERROR:
          return (
            <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-errorMedium px-2 py-1'}>
              ERROR
            </COBadge>
          );
        case JobStatusEnum.CANCELLED:
          return <COBadge variant={'filled'} type={'draft'}>CANCELLED</COBadge>;
        case JobStatusEnum.DRAFT:
          return <COBadge variant={'outlined'} type={'draft'}>DRAFT</COBadge>;
        default:
          return <COBadge variant={'outlined'} type={'processing'}>{job.jobStatus}</COBadge>;
      }
    };
    return (
      <div className={'flex flex-col gap-1'}>
        {badge()}
        <span className={'text-sm text-content-secondary'}>
          {job.processedCount}/{job.totalCount ?? '?'}
          {job.failedCount > 0 && `, failed: ${job.failedCount}`}
          {row.attemptCount > 1 && `, attempt ${row.attemptCount}`}
        </span>
        {job.errorMessage != null && <span className={'text-sm text-complementary-error break-all'}>{job.errorMessage}</span>}
      </div>
    );
  };

  const renderDatesColumn = (row: AdminAsyncJobOutDTO) => (
    <div className={'flex flex-col text-sm text-content-secondary'}>
      <span>{stringDateTimeWithLocaleFromApiStringInTz(row.job.createdAt, 'Europe/Prague')}</span>
      {row.job.finishedAt != null && <span>{stringDateTimeWithLocaleFromApiStringInTz(row.job.finishedAt, 'Europe/Prague')}</span>}
      {row.lockedBy != null && <span className={'text-content-tertiary'}>{row.lockedBy}</span>}
    </div>
  );

  const renderActionsColumn = (row: AdminAsyncJobOutDTO) => (
    <div className={'flex flex-row justify-end'}>
      {ACTIVE.includes(row.job.jobStatus) && (
        <RingilButton
          mode={'outlined'}
          label={tr('AdminJobs.cancel', 'Cancel')}
          onClick={() => cancelAsyncJob(row.job.jobId, () => tableRef.current?.reloadData(), null, { successToast: false })}
        />
      )}
    </div>
  );

  const columnConfig: ColumnConfig<AdminAsyncJobOutDTO>[] = [
    { id: 'job', header: 'AdminJobs.columnJob', headerDefault: 'Job', width: '25%', order: 1, render: renderJobColumn },
    { id: 'company', header: 'AdminJobs.columnCompany', headerDefault: 'Company / user', width: '25%', order: 2, render: renderCompanyColumn },
    { id: 'state', header: 'AdminJobs.columnState', headerDefault: 'State', width: '20%', order: 3, render: renderStateColumn },
    { id: 'category', header: 'AdminJobs.columnCategory', headerDefault: 'Category', width: '10%', order: 4, render: row => <COBadge variant={'outlined'} type={'neutral'}>{categoryLabel(row.jobCategory)}</COBadge> },
    { id: 'dates', header: 'AdminJobs.columnDates', headerDefault: 'Created / finished', width: '15%', order: 5, render: renderDatesColumn },
    { id: 'actions', header: '', headerDefault: '', width: '5%', order: 6, render: renderActionsColumn },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('AdminJobs.filterApplicationId', 'Job ID'),
      label: tr('AdminJobs.filterApplicationId', 'Job ID'),
      value: ['applicationId'],
      dataTestId: 'admin-jobs-filter-applicationId',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('AdminJobs.filterCompany', 'Company'),
      label: tr('AdminJobs.filterCompany', 'Company'),
      value: ['companyName'],
      dataTestId: 'admin-jobs-filter-company',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('AdminJobs.filterStatus', 'Status'),
      label: tr('AdminJobs.filterStatus', 'Status'),
      value: ['jobStatuses'],
      options: Object.values(JobStatusEnum).map(status => ({ label: status, value: status, active: true })),
      dataTestId: 'admin-jobs-filter-status',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('AdminJobs.filterType', 'Type'),
      label: tr('AdminJobs.filterType', 'Type'),
      value: ['jobTypes'],
      options: Object.values(JobTypeEnum).map(jobType => ({ label: typeLabel(jobType), value: jobType, active: true })),
      dataTestId: 'admin-jobs-filter-type',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('AdminJobs.filterCategory', 'Category'),
      label: tr('AdminJobs.filterCategory', 'Category'),
      value: ['categories'],
      options: Object.values(JobCategoryEnum).map(category => ({ label: categoryLabel(category), value: category, active: true })),
      dataTestId: 'admin-jobs-filter-category',
    },
  ];

  return (
    <AdminLayout title={'Async jobs'}>
      <BreadCrumbComponent items={[{ label: 'Async jobs' }]} />
      <div className={'flex flex-row items-center py-4'}>
        <COHeadline title={'Async jobs'} variant={'h2'} icon={<SvgRepeatIcon />} />
      </div>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'admin_jobs'}
        enableReload
      />
    </AdminLayout>
  );
};

export default withAuthenticationRequired(JobsMonitoringPage);
